import {
  Animation,
  Breadcrumb,
  Button,
  ComponentContainer,
  CustomSpacing,
  Flex,
  Icon,
  Input,
  Section,
  Text,
  theme,
} from '@genially/public-web-components';
import { FC, useContext, useEffect, useState } from 'react';

import type { BackgroundDTO } from 'core/infrastructure/parseBackground';
import { SearchParamContext } from '../../context/SearchContext';
import { parseBackground } from 'core/infrastructure/parseBackground';
import { rem } from 'polished';
import { translate } from 'core/infrastructure/translator';

interface ParagraphTemplatesSearcherInputProps {
  behaviorSettings?: {
    rowStyle: {
      background: BackgroundDTO;
      spacing: CustomSpacing;
      general: any;
      borders: any;
      hovers?: any;
    };
    layout_paragraphs: { layout: string; config: any };
  };
  animation?: keyof typeof theme.animations;
  breadcrumb?: { title: string; url: { path: string } };
  targetUrl?: string;
  samePage?: boolean;
  pagelangcode: {
    value: string;
  };
  buttonStyle?: {
    size: string;
    variant: 'academy' | 'danger' | 'primary' | 'secondary' | 'stroke' | 'tertiary' | 'strokeWhite';
  };
  placeholder?: string;
}

export const ParagraphTemplatesSearcherInput: FC<ParagraphTemplatesSearcherInputProps> = ({
  behaviorSettings,
  breadcrumb,
  animation = 'fadeInUp',
  targetUrl,
  pagelangcode,
  buttonStyle,
  samePage = false,
  placeholder,
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [localSearch, setLocalSearch] = useState<string>('');
  const [fullBreadcrumb, setBreadcrumb] = useState([] as any[]);
  const [searchParam, setSearchParam] = useState<string>('');

  const { setContextSearch } = useContext(SearchParamContext);
  const translateFromContent = (key: string) =>
    translate(pagelangcode ? pagelangcode.value : 'en', key);

  const titleText = translateFromContent('community_results_title');

  function handleButtonClick() {
    setLocalSearch(inputValue);
    setContextSearch(inputValue);
    setInputValue(inputValue);
  }

  function handleKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!inputValue) return;
    if (event.key === 'Enter') {
      handleButtonClick();
    }
  }

  useEffect(() => {
    if (!localSearch) return;

    if (targetUrl) {
      const finalUrl =
        pagelangcode.value === 'en'
          ? `${targetUrl}/?searchTerm=${localSearch}`
          : `/${pagelangcode.value}${targetUrl}/?searchTerm=${localSearch}`;
      window.location.href = finalUrl;
    }

    if (samePage) {
      history.replaceState(null, '', '?searchTerm=' + localSearch);
    }

    if (breadcrumb) {
      const fullBreadcrumb = [
        {
          url: breadcrumb?.url.path,
          title: breadcrumb?.title,
        },
      ].concat(localSearch ? [{ url: localSearch, title: decodeURIComponent(localSearch) }] : []);
      setBreadcrumb(fullBreadcrumb);
    }
  }, [localSearch]);

  useEffect(() => {
    const slug = new URLSearchParams(window.location.search);
    const searchTermFromUrl = slug.get('searchTerm') ?? '';
    setInputValue(decodeURIComponent(searchTermFromUrl));
    setSearchParam(searchTermFromUrl);

    if (breadcrumb) {
      const fullBreadcrumb = [
        {
          url: breadcrumb?.url.path,
          title: breadcrumb?.title,
        },
      ].concat(slug ? [{ url: '#', title: decodeURIComponent(searchTermFromUrl) }] : []);
      setBreadcrumb(fullBreadcrumb);
    }
  }, []);

  return (
    <Section background={parseBackground(behaviorSettings?.rowStyle?.background)}>
      <Animation type={animation}>
        <ComponentContainer customSpacing={behaviorSettings?.rowStyle?.spacing}>
          <Breadcrumb
            items={fullBreadcrumb.map(item => ({
              ...item,
              title: item.title
                ? item.title.charAt(0).toUpperCase() + item.title.slice(1)
                : item.title,
            }))}
          />
          {samePage && searchParam && (
            <Text
              variant="title3"
              $color={'Onyx'}
              style={{
                textAlign: 'center',
                textTransform: 'capitalize',
                paddingTop: rem(40),
                paddingBottom: rem(16),
              }}
            >
              {titleText}
              {' ' + decodeURIComponent(localSearch ? localSearch : searchParam)}
            </Text>
          )}
          <Flex
            flexDirection={'row'}
            alignItems="center"
            justifyContent="center"
            css={`
              position: relative;
              @media ${theme.device.tablet} {
                max-width: ${rem(600)};
                margin: auto;
              }
            `}
          >
            <Input
              size={'medium'}
              variant={'base'}
              placeholder={placeholder}
              value={inputValue}
              onChange={e => setInputValue((e.target as HTMLInputElement).value)}
              onKeyDown={handleKeyPress}
            />
            <div style={{ paddingLeft: '16px', position: 'absolute', right: 0 }}>
              <Button
                style={{ margin: '16px 0', display: 'flex', border: 'none' }}
                variant={buttonStyle && buttonStyle?.variant ? buttonStyle?.variant : 'stroke'}
                onClick={handleButtonClick}
              >
                <Icon name={'Search'} color={theme.colors.rOnyx}></Icon>
              </Button>
            </div>
          </Flex>
        </ComponentContainer>
      </Animation>
    </Section>
  );
};
